import { Link } from "react-router-dom";
import { Phone, ArrowRight, Award, ShieldCheck, Sparkles, Quote } from "lucide-react";
import { SITE, OWNER_PORTRAIT, ABOUT_BG } from "../lib/site";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity", text: "We do what we say, when we say. No upsells, no surprises." },
  { icon: Award, title: "Craft", text: "10 years of detail-obsessed painting, stucco and remodeling." },
  { icon: Sparkles, title: "Care", text: "Your home is treated like ours — clean job sites, respectful crews." },
];

export default function About() {
  return (
    <>
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end grain" data-testid="about-hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${ABOUT_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black" />
        <div className="container-luxury relative z-10 py-16">
          <p className="overline">About Us</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl text-white font-light leading-[1.05]">
            A decade of <span className="font-script text-[var(--gold)]">refined craftsmanship</span>
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="section" data-testid="about-story">
        <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-white/5">
              <img src={OWNER_PORTRAIT} alt="Santos Dario Zelaya" className="w-full h-full object-cover" />
            </div>
            <p className="mt-6 font-script text-3xl text-[var(--gold)]">Santos Dario Zelaya</p>
            <p className="text-sm uppercase tracking-[0.2em] text-white/50 mt-1">Owner · Founder</p>
          </div>
          <div className="lg:col-span-7">
            <p className="overline">Our Story</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl text-white font-light leading-tight">
              Born in Wesley Chapel. Built for homeowners who care about every detail.
            </h2>
            <div className="mt-8 space-y-6 text-white/70 leading-relaxed text-lg">
              <p>
                Zelaya's Design INC is owned by <span className="text-white">Santos Dario Zelaya</span> and proudly
                serves Wesley Chapel, Florida and surrounding areas. With{" "}
                <span className="text-white">10 years of experience</span>, we specialize in professional
                painting, stucco repair, remodeling and home improvement services.
              </p>
              <p>
                Our company is committed to quality, honesty, professionalism and customer satisfaction.
                We proudly offer a <span className="text-[var(--gold)]">1-year warranty</span> on our work — a
                promise we stand behind on every single project.
              </p>
              <p>
                From a single accent wall to a full home transformation, our crews bring the same precision,
                respect and care to every job. That's how we've earned our reputation — one neighbor at a time.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div key={i} className="bg-[#0A0A0A] p-8" data-testid={`value-${i}`}>
                    <Icon size={26} className="text-[var(--gold)]" />
                    <h3 className="mt-5 font-display text-xl text-white">{v.title}</h3>
                    <p className="mt-2 text-white/55 text-sm leading-relaxed">{v.text}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="about-estimate-btn">
                Get Free Estimate <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} className="btn-secondary" data-testid="about-call-btn">
                <Phone size={16} /> Call {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="section bg-[#0c0c0c]" data-testid="about-pullquote">
        <div className="container-luxury text-center">
          <Quote className="mx-auto text-[var(--gold)] opacity-60" size={40} />
          <p className="mt-8 font-display text-2xl md:text-4xl text-white italic max-w-4xl mx-auto leading-snug">
            "Every home I touch carries my name. That's why we measure twice, paint clean lines, and
            finish what we start. Honesty and craft — that's the standard."
          </p>
          <p className="mt-6 font-script text-2xl text-[var(--gold)]">— Santos Dario Zelaya, Owner</p>
        </div>
      </section>
    </>
  );
}
